import { settings } from '@/config';
import { createLogger } from '@/utils/logger';

/** One word of the current line, with the character range that selects it. */
interface Word {
  start: number;
  end: number;
  el: HTMLSpanElement;
}

/**
 * NARRATION + SYNCED SUBTITLES (2026-08-18, user: "behind of this our text that is reading by
 * female voice … realtime with subtitle, and that subtitle and voice has to sync").
 *
 * Speech comes from the browser's own `speechSynthesis`. That choice matters: it needs no API
 * key, ships no audio, costs no bandwidth, and starts instantly — and because story mode is
 * always entered by a deliberate press-and-hold, we already have the user gesture that browsers
 * require before they will speak.
 *
 * HOW THE SYNC IS REAL, not estimated: a `SpeechSynthesisUtterance` emits `boundary` events as
 * it speaks, each carrying the CHARACTER INDEX the voice has reached. Mapping that index onto
 * pre-measured word ranges lights each word at the moment it is actually said, so the subtitle
 * tracks the voice even when the engine speeds up, slows down or is a different voice entirely.
 *
 * FALLBACK: Firefox does not fire `boundary` for local voices. If nothing has arrived shortly
 * after speech starts, {@link startFallbackTimer} drives the highlight from a words-per-minute
 * estimate instead — visibly less tight, but never a dead, unlit subtitle.
 */
export class StoryNarrator {
  /** Fired when the whole story (every paragraph) has finished speaking. */
  onFinished?: () => void;
  /** Raised/lowered around speech so the caller can duck the music bed. */
  onSpeakingChange?: (speaking: boolean) => void;

  private readonly log = createLogger('narrator');
  private readonly root: HTMLDivElement;
  private readonly lineEl: HTMLDivElement;
  /** "Click to close" — revealed once the narration ends (see {@link finish}). */
  private readonly endEl: HTMLDivElement;

  private paragraphs: string[] = [];
  private index = 0;
  private words: Word[] = [];
  private spokenTo = -1;
  private active = false;
  private utterance: SpeechSynthesisUtterance | null = null;
  private voice: SpeechSynthesisVoice | null = null;
  private fallbackTimer: number | null = null;
  private keepAlive: number | null = null;
  private startTimer: number | null = null;
  private primed = false;

  constructor() {
    this.root = document.createElement('div');
    this.root.id = 'story-subtitles';
    this.root.setAttribute('aria-live', 'polite');

    this.lineEl = document.createElement('div');
    this.lineEl.className = 'story-sub__line';

    this.endEl = document.createElement('div');
    this.endEl.className = 'story-sub__end';
    this.endEl.textContent = settings.storyMode.endPrompt;

    // No title element (2026-09-01, user: "that small-caps title in shader mode — remove that
    // thing"): the story is just the narrated line; the chapter's headline already told the
    // viewer where they are before the smoke rolled in.
    this.root.append(this.lineEl, this.endEl);
    document.body.appendChild(this.root);

    // Voices load asynchronously in Chrome — the first `getVoices()` is usually empty.
    this.pickVoice();
    if (typeof speechSynthesis !== 'undefined') {
      speechSynthesis.addEventListener('voiceschanged', this.pickVoice);
      // PRIME on the first real gesture. Browsers gate speech behind user activation, and by the
      // time a story actually speaks we are ~2.5s past the press that opened it (hold, capture,
      // shatter, subtitle delay) — far enough that some engines refuse. Speaking one silent
      // utterance while the gesture is unambiguous unlocks the engine for the rest of the
      // session, and also forces the async voice list to populate.
      window.addEventListener('pointerdown', this.prime, { once: true });
      window.addEventListener('keydown', this.prime, { once: true });
    }
  }

  /** Unlock the speech engine (see the constructor). Harmless if it has already been unlocked. */
  private prime = (): void => {
    if (!StoryNarrator.supported || this.primed) return;
    this.primed = true;
    try {
      const u = new SpeechSynthesisUtterance(' ');
      u.volume = 0;
      speechSynthesis.speak(u);
      this.pickVoice();
    } catch {
      // Nothing to do — `speakCurrent` still tries for real, and falls back to timed subtitles.
    }
  };

  /** The voice the engine actually selected — surfaced in the debug panel, because "no voice"
   *  is nearly always either an empty voice list or a surprising pick. */
  get activeVoiceName(): string {
    if (!StoryNarrator.supported) return 'unsupported';
    return this.voice?.name ?? `engine default (${speechSynthesis.getVoices().length} voices)`;
  }

  /** Is the Web Speech API usable in this browser at all? */
  static get supported(): boolean {
    return typeof window !== 'undefined' && 'speechSynthesis' in window;
  }

  /**
   * Choose the most female-sounding English voice available.
   *
   * The Web Speech API exposes no gender field, so this is necessarily name-based: the curated
   * `settings.storyMode.voice.preferred` list first (those are the known female voices that ship
   * with Windows, macOS, iOS and Chrome), then any voice self-describing as female, then any
   * English voice at all. Whatever happens we end with SOMETHING rather than nothing.
   */
  private pickVoice = (): void => {
    if (!StoryNarrator.supported) return;
    const voices = speechSynthesis.getVoices();
    if (!voices.length) return;
    const cfg = settings.storyMode.voice;

    for (const wanted of cfg.preferred) {
      const hit = voices.find((v) => v.name.toLowerCase().includes(wanted.toLowerCase()));
      if (hit) {
        this.voice = hit;
        this.log.info(`narration voice: "${hit.name}" (${hit.lang})`);
        return;
      }
    }
    const female = voices.find(
      (v) => v.lang.toLowerCase().startsWith(cfg.lang) && /female|woman/i.test(v.name),
    );
    this.voice = female ?? voices.find((v) => v.lang.toLowerCase().startsWith(cfg.lang)) ?? voices[0]!;
    this.log.info(`narration voice (fallback): "${this.voice.name}" (${this.voice.lang})`);
  };

  /** Begin a story: show the panel, then speak + subtitle each paragraph in turn.
   *  (`title` is accepted for the call signature but no longer displayed — see constructor.) */
  start(_title: string, paragraphs: string[]): void {
    this.stop();
    this.paragraphs = paragraphs;
    this.index = 0;
    this.active = true;
    this.endEl.classList.remove('is-shown');
    this.root.classList.add('is-shown');
    this.speakCurrent();
  }

  private speakCurrent(): void {
    if (!this.active) return;
    const text = this.paragraphs[this.index];
    if (text === undefined) {
      this.finish();
      return;
    }

    this.renderLine(text);

    if (!StoryNarrator.supported || !settings.storyMode.voice.enabled) {
      // No speech engine: still tell the story, just on a reading-speed timer.
      this.startFallbackTimer();
      return;
    }

    const cfg = settings.storyMode.voice;
    const u = new SpeechSynthesisUtterance(text);
    if (this.voice) u.voice = this.voice;
    u.lang = this.voice?.lang ?? 'en-US';
    u.rate = cfg.rate;
    u.pitch = cfg.pitch;
    u.volume = cfg.volume;
    this.utterance = u;

    u.onboundary = (e) => {
      // `word` is what we want; some engines also emit `sentence`, which would jump the
      // highlight a whole line ahead if we honoured it.
      if (e.name && e.name !== 'word') return;
      // Real positions beat estimated ones: the paced timer is always running, so the first
      // boundary stands it down and the voice becomes the clock from here on.
      this.clearFallback();
      this.highlightAtChar(e.charIndex);
    };
    u.onend = () => {
      if (!this.active || this.utterance !== u) return;
      this.highlightAll();
      // Let the finished line be READ for a moment, then clear it out before the next arrives —
      // a swap in place reads as a machine updating a field; a hand-off reads as narration.
      this.startTimer = window.setTimeout(() => {
        if (!this.active) return;
        this.lineEl.classList.add('is-out');
        this.startTimer = window.setTimeout(() => {
          this.index += 1;
          this.speakCurrent();
        }, 620);
      }, 900);
    };
    u.onerror = (e) => {
      // `interrupted`/`canceled` are what `stop()` itself causes — not worth reporting.
      if (e.error !== 'interrupted' && e.error !== 'canceled') {
        this.log.warn(`speech failed (${e.error}) — falling back to timed subtitles.`);
        this.startFallbackTimer();
      }
    };

    this.onSpeakingChange?.(true);
    // DO NOT cancel() unconditionally here. Chrome drops an utterance queued in the same task as
    // a cancel(), which is very likely why the voice never arrived (2026-08-18, user: "female
    // voice is not coming"). Only clear the queue when something is actually on it, and let the
    // clear settle before queueing.
    const speakNow = () => {
      try {
        speechSynthesis.speak(u);
      } catch (error) {
        this.log.warn('speechSynthesis.speak threw — timed subtitles only.', error);
        this.startFallbackTimer();
      }
    };
    if (speechSynthesis.speaking || speechSynthesis.pending) {
      speechSynthesis.cancel();
      window.setTimeout(speakNow, 60);
    } else {
      speakNow();
    }
    this.log.info(
      `speaking paragraph ${this.index + 1}/${this.paragraphs.length} ` +
        `with "${this.voice?.name ?? 'engine default'}".`,
    );

    // CHROME KEEPALIVE: Chrome pauses synthesis after ~15s of continuous speech unless it is
    // poked. Paragraphs here are short enough that this rarely bites, but the cost is one no-op
    // call a few times a minute and the failure it prevents is the narration dying mid-sentence.
    if (this.keepAlive === null) {
      this.keepAlive = window.setInterval(() => {
        if (speechSynthesis.speaking && !speechSynthesis.paused) speechSynthesis.resume();
      }, 9000);
    }

    // START THE PACED REVEAL IMMEDIATELY, and let real boundary events take it over the moment
    // they arrive (the first one calls `clearFallback`).
    //
    // This ordering is the fix for the user's "not all lines revealing same time" (2026-08-18
    // v5). Previously the timer was only armed from `onstart`, so if the engine never spoke at
    // all — no `onstart`, no error, which is exactly what happens when speech is silently
    // unavailable — nothing ever paced the words, and the 3.2s watchdog dumped the ENTIRE line
    // on screen at once. Pacing unconditionally means the words always arrive one at a time; the
    // voice, when it works, simply becomes the more accurate clock.
    this.startFallbackTimer();
  }

  /** Split a paragraph into word spans, remembering each one's character range. */
  private renderLine(text: string): void {
    this.lineEl.innerHTML = '';
    this.words = [];
    this.spokenTo = -1;

    const re = /\S+/g;
    let match: RegExpExecArray | null;
    let last = 0;
    while ((match = re.exec(text)) !== null) {
      if (match.index > last) {
        this.lineEl.appendChild(document.createTextNode(text.slice(last, match.index)));
      }
      // PER-CHARACTER spans (2026-08-18, user: "character blurry animation for the revealing").
      // The word is still the SYNC unit — speech boundary events are word-granular — but each
      // character carries its own transition delay, so a word does not pop in as a block: its
      // letters resolve out of blur one after another, left to right. That cascade is what makes
      // the reveal feel typed-by-light rather than switched on.
      const el = document.createElement('span');
      el.className = 'story-sub__word';
      for (let ci = 0; ci < match[0].length; ci += 1) {
        const ch = document.createElement('span');
        ch.className = 'story-sub__char';
        ch.style.setProperty('--ci', String(ci));
        ch.textContent = match[0][ci]!;
        el.appendChild(ch);
      }
      this.lineEl.appendChild(el);
      this.words.push({ start: match.index, end: match.index + match[0].length, el });
      last = match.index + match[0].length;
    }
    if (last < text.length) this.lineEl.appendChild(document.createTextNode(text.slice(last)));

    // The words carry the entrance themselves now (each resolves out of blur as it is spoken),
    // so the line container only has to be brought back from its exit state.
    this.lineEl.classList.remove('is-out');
  }

  /** Light every word up to and including the one containing `charIndex`. */
  private highlightAtChar(charIndex: number): void {
    let target = -1;
    for (let i = 0; i < this.words.length; i += 1) {
      if (this.words[i]!.start <= charIndex) target = i;
      else break;
    }
    this.highlightTo(target);
  }

  private highlightTo(target: number): void {
    if (target === this.spokenTo) return;
    // Walk rather than rewrite: only the words that actually changed state get touched.
    if (target > this.spokenTo) {
      for (let i = this.spokenTo + 1; i <= target; i += 1) this.words[i]?.el.classList.add('is-said');
    } else {
      for (let i = this.spokenTo; i > target; i -= 1) this.words[i]?.el.classList.remove('is-said');
    }
    this.spokenTo = target;
  }

  private highlightAll(): void {
    this.highlightTo(this.words.length - 1);
  }

  /**
   * Drive the highlight from a words-per-minute estimate, for engines that never emit boundary
   * events (and for the no-speech path). Also advances to the next paragraph on its own, since
   * without an utterance there is no `onend` to wait for.
   */
  private startFallbackTimer(): void {
    this.clearFallback();
    const cfg = settings.storyMode.voice;
    const perWordMs = (60 / Math.max(cfg.fallbackWpm, 40)) * 1000 / Math.max(cfg.rate, 0.3);
    let i = this.spokenTo + 1;
    // Whether a real utterance is in flight and therefore owns advancing (via `onend`). Read at
    // ARM time from whether we queued one for this line, not from `speechSynthesis.speaking`,
    // which is still false in the moments between `speak()` and the voice actually starting.
    const speaking = this.utterance !== null;

    this.fallbackTimer = window.setInterval(() => {
      if (!this.active) return this.clearFallback();
      if (i >= this.words.length) {
        this.clearFallback();
        // When a real utterance is running, IT owns advancing (via `onend`) — the timer was only
        // ever here to move the highlight. Advancing from both would skip a paragraph.
        if (!speaking) {
          this.lineEl.classList.add('is-out');
          this.startTimer = window.setTimeout(() => {
            this.index += 1;
            this.speakCurrent();
          }, 900);
        }
        return;
      }
      this.highlightTo(i);
      i += 1;
    }, perWordMs);
  }

  private clearFallback(): void {
    if (this.fallbackTimer !== null) {
      clearInterval(this.fallbackTimer);
      this.fallbackTimer = null;
    }
  }

  private finish(): void {
    this.onSpeakingChange?.(false);
    // The story is over, so tell the user how to leave (2026-08-18, user: "once the story ended
    // it automatically tells user to close it"). The ring cursor already reads CLOSE; this is the
    // prompt that sends them looking at it.
    this.endEl.classList.add('is-shown');
    this.onFinished?.();
  }

  /** Stop speaking, clear the subtitle and hide the panel. Safe to call at any time. */
  stop(): void {
    this.active = false;
    this.clearFallback();
    if (this.startTimer !== null) {
      clearTimeout(this.startTimer);
      this.startTimer = null;
    }
    if (this.keepAlive !== null) {
      clearInterval(this.keepAlive);
      this.keepAlive = null;
    }
    if (this.utterance) {
      // Drop the handlers BEFORE cancelling, or `onend` fires for the cancellation itself and
      // queues the next paragraph of a story we are in the middle of closing.
      this.utterance.onend = null;
      this.utterance.onboundary = null;
      this.utterance.onerror = null;
      this.utterance.onstart = null;
      this.utterance = null;
    }
    if (StoryNarrator.supported) speechSynthesis.cancel();
    this.endEl.classList.remove('is-shown');
    this.root.classList.remove('is-shown');
    this.onSpeakingChange?.(false);
  }

  dispose(): void {
    this.stop();
    if (StoryNarrator.supported) {
      speechSynthesis.removeEventListener('voiceschanged', this.pickVoice);
    }
    window.removeEventListener('pointerdown', this.prime);
    window.removeEventListener('keydown', this.prime);
    this.root.remove();
  }
}
